import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Hammer, ChevronRight } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { supabase } from '@/integrations/supabase/client';
import { ImageManager } from '@/lib/imageManager';

interface Category {
  id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  icon_name: string | null;
  coming_soon?: boolean;
  productCount?: number;
}

const getCategoryIcon = (category: Category) => {
  if (category.icon_name) {
    const IconComponent = (Icons as any)[category.icon_name];
    if (IconComponent) return IconComponent;
  }
  return Hammer; // Default icon
};

const Products = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('*')
          .order('coming_soon', { ascending: true, nullsFirst: true })
          .order('name');

        if (categoriesError) {
          console.error('Error fetching categories:', categoriesError);
          return;
        }

        // Get product counts for each category
        const categoriesWithCounts = await Promise.all(
          (categoriesData || []).map(async (category) => {
            const { data: count } = await supabase
              .rpc('get_product_count_by_category', { category_id_param: category.id });
            return {
              ...category,
              productCount: count || 0,
            };
          })
        );

        setCategories(categoriesWithCounts);

        // Track view_item_list event for products page
        if (typeof window !== 'undefined' && window.gtag && categoriesWithCounts.length > 0) {
          window.gtag('event', 'view_item_list', {
            item_list_name: 'All Categories',
            items: categoriesWithCounts.slice(0, 10).map((category, index) => ({
              item_list_id: category.id,
              item_list_name: category.name,
              index: index
            }))
          });
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-16 lg:pt-8 pb-12">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-foreground mb-4">קטגוריות</h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                עיין במגוון הרחב של חומרי הבנייה והעיצוב שלנו
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-0">
                    <div className="w-full h-48 bg-gray-200 rounded-t-lg"></div>
                    <div className="p-6">
                      <div className="h-6 bg-gray-200 rounded mb-3"></div>
                      <div className="h-4 bg-gray-200 rounded mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-4 lg:pt-8 pb-12">
        <div className="container mx-auto px-4">
          {/* Breadcrumbs */}
          <div className="mb-6 mt-0" dir="rtl">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/">
                    בית
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator>
                  <ChevronRight className="h-4 w-4 rotate-180" />
                </BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbPage>קטגוריות</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4 hebrew-heading">קטגוריות</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              עיין במגוון הרחב של חומרי הבנייה והעיצוב שלנו, מקוטלגים בקפידה עבורך
            </p>
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => {
              const IconComponent = getCategoryIcon(category);
              const isComingSoon = category.coming_soon === true;

              return (
                <a
                  key={category.id}
                  href={isComingSoon ? undefined : `/category/${encodeURIComponent(category.name)}`}
                  onClick={(e) => {
                    if (isComingSoon) {
                      e.preventDefault();
                      return;
                    }
                    if (!e.ctrlKey && !e.metaKey) {
                      e.preventDefault();
                      navigate(`/category/${encodeURIComponent(category.name)}`);
                    }
                  }}
                  className={`block ${isComingSoon ? 'pointer-events-none' : ''}`}
                  target={isComingSoon ? undefined : "_blank"}
                  rel={isComingSoon ? undefined : "noopener noreferrer"}
                >
                  <Card
                    className={`group transition-all duration-300 ${isComingSoon
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:shadow-xl transform hover:-translate-y-2 cursor-pointer'
                      }`}
                  >
                    <CardContent className="p-0">
                      <div className="relative">
                        {category.image_url ? (
                          <img
                            src={ImageManager.getImageUrl(category.image_url, 'category')}
                            alt={category.name}
                            className={`w-full h-48 object-cover rounded-t-lg ${isComingSoon ? 'grayscale' : ''
                              }`}
                            onError={(e) => ImageManager.handleImageError(e, 'category')}
                          />
                        ) : (
                          <div className={`w-full h-48 bg-gradient-to-br from-primary to-secondary rounded-t-lg flex items-center justify-center ${isComingSoon ? 'grayscale' : ''
                            }`}>
                            <IconComponent className="h-16 w-16 text-white" />
                          </div>
                        )}

                        {isComingSoon ? (
                          <Badge className="absolute top-4 right-4 bg-amber-500 text-white">
                            בקרוב
                          </Badge>
                        ) : (
                          <Badge className="absolute top-4 right-4 bg-lime text-lime-foreground">
                            {category.productCount || 0} מוצרים
                          </Badge>
                        )}
                      </div>

                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-lime/10 rounded-lg flex items-center justify-center">
                            <IconComponent className="h-5 w-5 text-lime" />
                          </div>
                          <h3 className="text-xl font-bold text-foreground">{category.name}</h3>
                        </div>

                        <p className="text-muted-foreground mb-4">{category.description || 'קטגוריה מגוונת עם מוצרים איכותיים'}</p>

                        {isComingSoon ? (
                          <div className="flex justify-end items-center">
                            <span className="text-amber-500 font-medium">
                              תהיה זמינה בקרוב
                            </span>
                          </div>
                        ) : (
                          <div className="flex justify-end items-center">
                            <span className="text-lime hover:text-lime/80 font-medium group-hover:translate-x-1 transition-transform">
                              צפה בקטגוריה ←
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </a>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="text-center mt-16">
            <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-4">לא מצאת מה שחיפשת?</h2>
              <p className="text-lg mb-6 opacity-90">
                צור קשר איתנו ואנחנו נעזור לך למצוא בדיוק מה שאתה צריך
              </p>
              <button
                onClick={() => navigate('/contact')}
                className="bg-lime text-lime-foreground px-8 py-3 rounded-lg font-medium hover:bg-lime/90 transition-colors"
              >
                צור קשר
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Products;